import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import { useAuth } from '../context/AuthContext';
import { LineChart, Line, ResponsiveContainer } from 'recharts';

interface TickerData {
  symbol: string;
  name: string;
  fullName: string;
  price: number;
  change: number;
  changePct: number;
  prevClose: number | null;
  sparkline: number[];
}

const MarketTickerBar: React.FC = () => {
  const [tickers, setTickers] = useState<TickerData[]>([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  const fetchTickers = useCallback(async () => {
    try {
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch('/api/market/tickers', { headers });
      if (response.ok) {
        const data = await response.json();
        if (data.tickers && data.tickers.length > 0) {
          setTickers(data.tickers);
        }
      }
    } catch (error) {
      console.error('Error fetching market tickers:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTickers();
    const interval = setInterval(fetchTickers, 60000);
    return () => clearInterval(interval);
  }, [fetchTickers]);

  const formatPrice = (price: number, symbol: string) => {
    if (symbol === '^VIX') return price.toFixed(2);
    if (symbol === 'BTC-USD') return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  if (loading && tickers.length === 0) {
    return (
      <Box sx={{
        display: 'flex',
        gap: 2,
        px: 2,
        py: 1,
        mb: 2,
        bgcolor: 'rgba(0,0,0,0.3)',
        borderRadius: 2,
        overflow: 'hidden',
      }}>
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} variant="rounded" width={160} height={48} sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} />
        ))}
      </Box>
    );
  }

  if (tickers.length === 0) return null;

  return (
    <Box sx={{
      display: 'flex',
      gap: { xs: 1, md: 1.5 },
      px: { xs: 1, md: 2 },
      py: 1,
      mb: 2,
      bgcolor: 'rgba(0,0,0,0.35)',
      borderRadius: 2,
      overflowX: 'auto',
      '&::-webkit-scrollbar': { height: 4 },
      '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 },
    }}>
      {tickers.map((ticker) => {
        const isPositive = ticker.changePct >= 0;
        const color = isPositive ? '#4caf50' : '#f44336';
        const sparkData = ticker.sparkline.map((v) => ({ v }));

        return (
          <Box
            key={ticker.symbol}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.5,
              minWidth: 'fit-content',
              borderRight: '1px solid rgba(255,255,255,0.06)',
              '&:last-child': { borderRight: 'none' },
            }}
          >
            <Box sx={{ width: 50, height: 28, flexShrink: 0 }}>
              {sparkData.length > 1 && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={sparkData}>
                    <Line
                      type="monotone"
                      dataKey="v"
                      stroke={color}
                      dot={false}
                      strokeWidth={1.5}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: 'rgba(255,255,255,0.85)',
                  lineHeight: 1.2,
                  display: 'block',
                  whiteSpace: 'nowrap',
                  fontSize: '0.7rem',
                }}
              >
                {ticker.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: '#fff',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    fontSize: '0.8rem',
                  }}
                >
                  {formatPrice(ticker.price, ticker.symbol)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color,
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    fontSize: '0.65rem',
                  }}
                >
                  {isPositive ? '+' : ''}{ticker.changePct.toFixed(2)}%
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default MarketTickerBar;
